import type { Location } from '@disco/api/mixpanel'

import React, { useState } from 'react'
import { useRegionCookieConsentContext } from 'contexts/cookie-consent'
import { ThemeProvider } from 'contexts/theme'
import CookiePreferences from 'components/modals/cookie-preferences'
import { ThirdPartyScripts } from 'components/third-party-scripts'
import Button from 'components/ui/button'
import ButtonContainer from 'components/ui/button-container'
import useScriptsAllowed from 'hooks/use-scripts-allowed'
import styles from './index.module.css'

type ScriptType = 'GA' | 'Mixpanel' | 'Intercom'
type Props = {
  location: Location
  scriptParams?: Partial<Record<ScriptType, Record<string, string>>>
}
const hiddenBannerLocations: Array<Location> = ['Explorer', 'Settings']

const CookieBanner = ({ location, scriptParams }: Props): React.ReactNode => {
  const [showCookiePreferences, setShowCookiePreferences] = useState(false)
  const { consent, acceptCookies } = useRegionCookieConsentContext()

  const accept = (statistics = true) => {
    acceptCookies({
      necessary: true,
      statistics,
    })
  }

  const [regionDetected, scriptsAllowed] = useScriptsAllowed()
  const showBanner =
    regionDetected &&
    !scriptsAllowed &&
    !consent.necessary &&
    !hiddenBannerLocations.includes(location)
  return (
    <>
      <ThirdPartyScripts
        regionDetected={regionDetected}
        scriptsAllowed={scriptsAllowed}
        location={location}
        scriptParams={scriptParams}
      />
      {showBanner && (
        <ThemeProvider
          value={{
            theme: 'light',
          }}
        >
          <div className={styles.cookieBanner}>
            <div className={styles.cookieBannerModalCont}>
              <div className={styles.cookieBannerModal}>
                <h3>
                  Welcome to DISCO
                  {/* Whitelabel */}
                </h3>
                <p>
                  As described in our{' '}
                  <a
                    target="_blank"
                    rel="noopener noreferrer"
                    href="https://disco.ac/cookies"
                  >
                    Cookie Policy
                  </a>
                  , we use cookies to deliver, maintain and improve our app. You
                  can change your cookie preferences below. Your cookie
                  preferences will apply to other DISCOs you visit.
                </p>
                <div className={styles.cookieBannerModalBtn}>
                  <ButtonContainer alignRight>
                    <Button
                      appearance="subtle"
                      caption="Cookie preferences"
                      onClick={() => setShowCookiePreferences(true)}
                    />
                    <Button
                      appearance="primary"
                      onClick={() => accept()}
                      caption="Accept all cookies"
                    />
                  </ButtonContainer>
                </div>
              </div>
            </div>
          </div>
        </ThemeProvider>
      )}

      {showCookiePreferences && (
        <CookiePreferences
          cookieLink="https://disco.ac/cookies"
          privacyPoliciesLink="https://disco.ac/privacy"
          onClose={() => setShowCookiePreferences(false)}
          onSubmit={({ statistics }) => {
            accept(statistics)
            setShowCookiePreferences(false)
          }}
        />
      )}
    </>
  )
}

export default CookieBanner
